import type { InkStroke, InkStyle, StrokePoint } from '@shared/notebook_model';
import { DEFAULT_INK_STYLE, makeStrokeId } from '@shared/notebook_model';
import { InkLayer } from './ink_layer';
import { PageRenderer, type PageLayout } from './page_renderer';
import { TextureLayer } from './texture_layer';
import {
  createDefaultBenchDocument,
  loadBenchDocument,
  saveBenchDocument,
  type BenchDocument,
  type BenchPad,
  type BenchTool,
} from './experimental_bench_store';

const PAGE_WIDTH = 670;
const PAGE_HEIGHT = 840;
const BENCH_PAGE_COLOR = '#8b7355';

const BENCH_LAYOUT: PageLayout = {
  x: 0,
  y: 0,
  width: PAGE_WIDTH,
  height: PAGE_HEIGHT,
  marginLeft: 56,
  marginRight: 44,
  marginTop: 56,
  marginBottom: 56,
};

const TOOL_LABELS: Record<BenchTool, string> = {
  ink: 'Ink',
  pencil: 'Pencil',
  marker: 'Marker',
  chalk: 'Chalk',
  eraser: 'Erase',
};

const COLOR_SWATCHES = ['#2c1810', '#6f2d1f', '#2f5b4f', '#2e4f76', '#7b6443'];

function cloneStroke(stroke: InkStroke): InkStroke {
  return {
    ...stroke,
    style: { ...stroke.style },
    points: stroke.points.map(point => ({ ...point })),
  };
}

function strokeMapFromPad(pad: BenchPad): Map<string, InkStroke> {
  return new Map(pad.strokes.map(stroke => [stroke.id, cloneStroke(stroke)]));
}

export class ExperimentalBenchApp {
  private _container: HTMLElement;
  private _canvas: HTMLCanvasElement;
  private _ctx: CanvasRenderingContext2D;
  private _toolbar: HTMLDivElement;
  private _padStrip: HTMLDivElement;
  private _padTitle: HTMLInputElement;
  private _status: HTMLSpanElement;
  private _sizeInput: HTMLInputElement;
  private _toolButtons = new Map<BenchTool, HTMLButtonElement>();
  private _colorButtons = new Map<string, HTMLButtonElement>();

  private _page: PageRenderer;
  private _texture: TextureLayer;
  private _ink: InkLayer;

  private _doc: BenchDocument = createDefaultBenchDocument();
  private _pageScale = 1;
  private _pageOffsetX = 0;
  private _pageOffsetY = 0;

  private _activePointerId: number | null = null;
  private _activeStrokeId: string | null = null;
  private _hoverPoint: { x: number; y: number } | null = null;

  private _rafHandle = 0;
  private _saveTimer = 0;
  private _saveState: 'saved' | 'saving' | 'unsaved' = 'saved';

  constructor(container: HTMLElement) {
    this._container = container;
    this._container.innerHTML = '';
    this._container.style.position = 'relative';
    this._container.style.width = '100%';
    this._container.style.height = '100%';
    this._container.style.overflow = 'hidden';

    this._canvas = document.createElement('canvas');
    this._canvas.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; touch-action:none;';
    this._container.appendChild(this._canvas);

    const ctx = this._canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D not available');
    this._ctx = ctx;
    this._page = new PageRenderer(this._ctx);
    this._texture = new TextureLayer(this._ctx);

    this._toolbar = this._buildToolbar();
    this._container.appendChild(this._toolbar);

    this._ink = new InkLayer(this._ctx, PAGE_WIDTH, PAGE_HEIGHT, window.devicePixelRatio || 1);
    this._ink.setGrainCanvas(this._texture.grainCanvas(BENCH_PAGE_COLOR));

    this._bindEvents();
    this._resize();
  }

  async init(): Promise<void> {
    this._doc = await loadBenchDocument();
    this._syncControlsFromDoc();
    this._syncPadTitle();
    this._loadActivePadIntoInk();
    this._renderLoop();
  }

  dispose(): void {
    window.removeEventListener('resize', this._resize);
    this._canvas.removeEventListener('pointerdown', this._onPointerDown);
    this._canvas.removeEventListener('pointermove', this._onPointerMove);
    this._canvas.removeEventListener('pointerup', this._onPointerUp);
    this._canvas.removeEventListener('pointercancel', this._onPointerCancel);
    if (this._rafHandle) cancelAnimationFrame(this._rafHandle);
    if (this._saveTimer) window.clearTimeout(this._saveTimer);
    void saveBenchDocument(this._doc);
  }

  private _buildToolbar(): HTMLDivElement {
    const style = document.createElement('style');
    style.textContent = `
      .ox-bench-toolbar {
        position: absolute;
        top: max(12px, env(safe-area-inset-top));
        left: 50%;
        transform: translateX(-50%);
        width: min(980px, calc(100% - 24px));
        padding: 14px 16px;
        border-radius: 22px;
        border: 1px solid rgba(208, 180, 132, 0.35);
        background:
          linear-gradient(135deg, rgba(34, 25, 18, 0.95), rgba(52, 38, 25, 0.9)),
          radial-gradient(circle at top left, rgba(201, 160, 96, 0.16), transparent 45%);
        box-shadow: 0 14px 40px rgba(0, 0, 0, 0.28);
        color: #f2e6cb;
        font-family: "Iowan Old Style", "Palatino Linotype", Georgia, serif;
        z-index: 2;
      }
      .ox-bench-toolbar,
      .ox-bench-toolbar * {
        box-sizing: border-box;
      }
      .ox-bench-grid {
        display: grid;
        grid-template-columns: minmax(190px, 1fr) minmax(260px, 1.2fr) minmax(220px, 1fr);
        gap: 14px;
        align-items: start;
      }
      .ox-bench-brand h1 {
        font-size: 21px;
        line-height: 1.1;
        letter-spacing: 0.02em;
        margin: 0;
      }
      .ox-bench-brand p {
        margin: 6px 0 0;
        font-size: 13px;
        line-height: 1.4;
        color: rgba(242, 230, 203, 0.76);
      }
      .ox-bench-group-label {
        display: block;
        margin-bottom: 8px;
        font-size: 11px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: rgba(242, 230, 203, 0.64);
      }
      .ox-bench-pad-strip,
      .ox-bench-inline,
      .ox-bench-swatches,
      .ox-bench-tools {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
      .ox-bench-chip,
      .ox-bench-link,
      .ox-bench-title,
      .ox-bench-size {
        border-radius: 999px;
        border: 1px solid rgba(232, 206, 160, 0.18);
        background: rgba(255, 248, 232, 0.08);
        color: #f8ecd2;
      }
      .ox-bench-chip,
      .ox-bench-link {
        padding: 8px 12px;
        font: inherit;
        font-size: 13px;
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
      }
      .ox-bench-chip:hover,
      .ox-bench-link:hover {
        background: rgba(255, 248, 232, 0.14);
        border-color: rgba(232, 206, 160, 0.38);
        transform: translateY(-1px);
      }
      .ox-bench-chip.is-active {
        background: linear-gradient(135deg, rgba(205, 171, 117, 0.28), rgba(121, 83, 39, 0.25));
        border-color: rgba(232, 206, 160, 0.5);
      }
      .ox-bench-link {
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .ox-bench-title {
        width: 100%;
        padding: 8px 14px;
        font: inherit;
        font-size: 13px;
        outline: none;
      }
      .ox-bench-title::placeholder {
        color: rgba(248, 236, 210, 0.48);
      }
      .ox-bench-size {
        width: 100%;
        padding: 4px 10px;
      }
      .ox-bench-size input {
        width: 100%;
      }
      .ox-bench-swatch {
        width: 28px;
        height: 28px;
        border-radius: 999px;
        border: 2px solid transparent;
        cursor: pointer;
        box-shadow: inset 0 0 0 1px rgba(20, 14, 10, 0.35);
      }
      .ox-bench-swatch.is-active {
        border-color: #f6e5bb;
      }
      .ox-bench-status {
        display: inline-flex;
        align-items: center;
        min-height: 30px;
        font-size: 12px;
        color: rgba(242, 230, 203, 0.72);
      }
      @media (max-width: 860px) {
        .ox-bench-grid {
          grid-template-columns: 1fr;
        }
      }
    `;
    document.head.appendChild(style);

    const toolbar = document.createElement('div');
    toolbar.className = 'ox-bench-toolbar';

    const grid = document.createElement('div');
    grid.className = 'ox-bench-grid';
    toolbar.appendChild(grid);

    const brand = document.createElement('div');
    brand.className = 'ox-bench-brand';
    brand.innerHTML = `
      <h1>Experimental Bench</h1>
      <p>Standalone scratch surface with the journal’s page and ink feel, but without spells, hints, agents, or shape logic.</p>
    `;
    grid.appendChild(brand);

    const pads = document.createElement('div');
    pads.innerHTML = '<span class="ox-bench-group-label">Pads</span>';
    this._padStrip = document.createElement('div');
    this._padStrip.className = 'ox-bench-pad-strip';
    pads.appendChild(this._padStrip);
    grid.appendChild(pads);

    const controls = document.createElement('div');
    controls.innerHTML = '<span class="ox-bench-group-label">Controls</span>';

    const titleRow = document.createElement('div');
    titleRow.className = 'ox-bench-inline';
    this._padTitle = document.createElement('input');
    this._padTitle.className = 'ox-bench-title';
    this._padTitle.placeholder = 'Pad title';
    this._padTitle.addEventListener('change', () => {
      const pad = this._activePad();
      if (!pad) return;
      const nextTitle = this._padTitle.value.trim() || pad.title;
      pad.title = nextTitle;
      pad.updatedAt = Date.now();
      this._padTitle.value = nextTitle;
      this._markDirty();
      this._renderPadButtons();
    });
    titleRow.appendChild(this._padTitle);
    controls.appendChild(titleRow);

    const toolRow = document.createElement('div');
    toolRow.className = 'ox-bench-tools';
    (['ink', 'pencil', 'marker', 'chalk', 'eraser'] as BenchTool[]).forEach(tool => {
      const button = this._makeChip(TOOL_LABELS[tool], () => {
        this._doc.toolPrefs.tool = tool;
        this._markDirty();
        this._syncControlsFromDoc();
      });
      this._toolButtons.set(tool, button);
      toolRow.appendChild(button);
    });
    controls.appendChild(toolRow);

    const swatches = document.createElement('div');
    swatches.className = 'ox-bench-swatches';
    COLOR_SWATCHES.forEach(color => {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'ox-bench-swatch';
      swatch.style.background = color;
      swatch.addEventListener('click', () => {
        this._doc.toolPrefs.color = color;
        this._markDirty();
        this._syncControlsFromDoc();
      });
      this._colorButtons.set(color, swatch);
      swatches.appendChild(swatch);
    });
    controls.appendChild(swatches);

    const sizeWrap = document.createElement('label');
    sizeWrap.className = 'ox-bench-size';
    this._sizeInput = document.createElement('input');
    this._sizeInput.type = 'range';
    this._sizeInput.min = '1';
    this._sizeInput.max = '14';
    this._sizeInput.step = '0.5';
    this._sizeInput.addEventListener('input', () => {
      this._doc.toolPrefs.size = parseFloat(this._sizeInput.value);
      this._markDirty();
    });
    sizeWrap.appendChild(this._sizeInput);
    controls.appendChild(sizeWrap);

    const actionRow = document.createElement('div');
    actionRow.className = 'ox-bench-inline';
    actionRow.appendChild(this._makeChip('+ Pad', () => this._addPad()));
    actionRow.appendChild(this._makeChip('Clear Pad', () => this._clearPad()));
    this._status = document.createElement('span');
    this._status.className = 'ox-bench-status';
    actionRow.appendChild(this._status);
    const journalLink = document.createElement('a');
    journalLink.className = 'ox-bench-link';
    journalLink.href = './index.html';
    journalLink.textContent = 'Journal';
    actionRow.appendChild(journalLink);
    controls.appendChild(actionRow);

    grid.appendChild(controls);
    return toolbar;
  }

  private _makeChip(label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ox-bench-chip';
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  private _bindEvents(): void {
    this._canvas.addEventListener('pointerdown', this._onPointerDown, { passive: false });
    this._canvas.addEventListener('pointermove', this._onPointerMove, { passive: false });
    this._canvas.addEventListener('pointerup', this._onPointerUp, { passive: false });
    this._canvas.addEventListener('pointercancel', this._onPointerCancel, { passive: false });
    window.addEventListener('resize', this._resize);
  }

  private _resize = (): void => {
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    this._canvas.width = Math.round(width * dpr);
    this._canvas.height = Math.round(height * dpr);
    this._canvas.style.width = `${width}px`;
    this._canvas.style.height = `${height}px`;

    const toolbarHeight = Math.ceil(this._toolbar.getBoundingClientRect().height || 96);
    const availableWidth = Math.max(width - 24, 320);
    const availableHeight = Math.max(height - toolbarHeight - 24, 240);
    this._pageScale = Math.min(1, availableWidth / PAGE_WIDTH, availableHeight / PAGE_HEIGHT);
    this._pageOffsetX = Math.round((width - PAGE_WIDTH * this._pageScale) / 2);
    this._pageOffsetY = Math.round(toolbarHeight + 12 + Math.max(0, (availableHeight - PAGE_HEIGHT * this._pageScale) / 2));

    this._ink.resize(PAGE_WIDTH, PAGE_HEIGHT, dpr);
    this._ink.setGrainCanvas(this._texture.grainCanvas(BENCH_PAGE_COLOR));
  };

  private _activePad(): BenchPad | undefined {
    return this._doc.pads.find(pad => pad.id === this._doc.activePadId);
  }

  private _loadActivePadIntoInk(): void {
    const pad = this._activePad();
    this._ink.setStrokes(pad ? strokeMapFromPad(pad) : new Map());
    this._syncPadTitle();
    this._renderPadButtons();
  }

  private _syncPadTitle(): void {
    const pad = this._activePad();
    this._padTitle.value = pad?.title ?? '';
  }

  private _syncControlsFromDoc(): void {
    this._sizeInput.value = String(this._doc.toolPrefs.size);
    this._toolButtons.forEach((button, tool) => {
      button.classList.toggle('is-active', tool === this._doc.toolPrefs.tool);
    });
    this._colorButtons.forEach((button, color) => {
      button.classList.toggle('is-active', color === this._doc.toolPrefs.color);
    });
    this._status.textContent = this._saveState === 'saving'
      ? 'Saving…'
      : this._saveState === 'unsaved'
        ? 'Unsaved'
        : 'Saved';
  }

  private _renderPadButtons(): void {
    this._padStrip.innerHTML = '';
    this._doc.pads.forEach((pad, index) => {
      const button = this._makeChip(pad.title || `Scratch ${index + 1}`, () => {
        this._doc.activePadId = pad.id;
        this._markDirty();
        this._loadActivePadIntoInk();
      });
      button.classList.toggle('is-active', pad.id === this._doc.activePadId);
      this._padStrip.appendChild(button);
    });
  }

  private _markDirty(): void {
    this._saveState = 'unsaved';
    this._syncControlsFromDoc();
    if (this._saveTimer) window.clearTimeout(this._saveTimer);
    this._saveTimer = window.setTimeout(() => {
      this._saveState = 'saving';
      this._syncControlsFromDoc();
      saveBenchDocument(this._doc)
        .then(() => {
          this._saveState = 'saved';
          this._syncControlsFromDoc();
        })
        .catch(err => {
          console.warn('[bench] save failed:', err);
          this._saveState = 'unsaved';
          this._syncControlsFromDoc();
        });
    }, 250);
  }

  private _addPad(): void {
    const count = this._doc.pads.length;
    const pad: BenchPad = {
      id: `bench_${Date.now().toString(36)}`,
      title: `Scratch ${String.fromCharCode(65 + (count % 26))}`,
      strokes: [],
      updatedAt: Date.now(),
    };
    this._doc.pads.push(pad);
    this._doc.activePadId = pad.id;
    this._markDirty();
    this._loadActivePadIntoInk();
  }

  private _clearPad(): void {
    const pad = this._activePad();
    if (!pad) return;
    if (!window.confirm(`Clear "${pad.title}"?`)) return;
    pad.strokes = [];
    pad.updatedAt = Date.now();
    this._ink.clearPage();
    this._markDirty();
  }

  private _currentInkStyle(): InkStyle {
    return {
      ...DEFAULT_INK_STYLE,
      color: this._doc.toolPrefs.color,
      size: this._doc.toolPrefs.size,
      style: this._doc.toolPrefs.tool === 'eraser' ? 'eraser' : this._doc.toolPrefs.tool,
      opacity: this._doc.toolPrefs.tool === 'marker' ? 0.78 : DEFAULT_INK_STYLE.opacity,
    };
  }

  private _toPagePoint(e: PointerEvent, clamp = false): { x: number; y: number } | null {
    const rawX = (e.clientX - this._pageOffsetX) / this._pageScale;
    const rawY = (e.clientY - this._pageOffsetY) / this._pageScale;
    if (clamp) {
      return {
        x: Math.max(0, Math.min(PAGE_WIDTH, rawX)),
        y: Math.max(0, Math.min(PAGE_HEIGHT, rawY)),
      };
    }
    if (rawX < 0 || rawY < 0 || rawX > PAGE_WIDTH || rawY > PAGE_HEIGHT) return null;
    return { x: rawX, y: rawY };
  }

  private _toStrokePoint(e: PointerEvent, clamp = false): StrokePoint | null {
    const pt = this._toPagePoint(e, clamp);
    if (!pt) return null;
    return {
      x: pt.x,
      y: pt.y,
      pressure: e.pressure > 0 ? e.pressure : 0.5,
      tiltX: e.tiltX ?? 0,
      tiltY: e.tiltY ?? 0,
      twist: e.twist ?? 0,
      timestamp: e.timeStamp,
    };
  }

  private _eraserRadius(): number {
    return Math.max(10, this._doc.toolPrefs.size * 3.2);
  }

  private _eraseAt(x: number, y: number): void {
    const hitIds = this._ink.strokesInRadius(x, y, this._eraserRadius());
    if (hitIds.length === 0) return;
    const pad = this._activePad();
    if (!pad) return;

    const remove = new Set(hitIds);
    pad.strokes = pad.strokes.filter(stroke => !remove.has(stroke.id));
    pad.updatedAt = Date.now();
    hitIds.forEach(id => this._ink.removeStroke(id));
    this._markDirty();
  }

  private _finishStroke(commit = true): void {
    const pad = this._activePad();
    if (!pad) return;

    if (this._doc.toolPrefs.tool === 'eraser') {
      this._activePointerId = null;
      this._activeStrokeId = null;
      return;
    }

    if (!commit) {
      this._ink.cancelStroke();
      this._activePointerId = null;
      this._activeStrokeId = null;
      return;
    }

    const stroke = this._ink.commitStroke();
    this._activePointerId = null;
    this._activeStrokeId = null;
    if (!stroke) return;

    pad.strokes.push(cloneStroke(stroke));
    pad.updatedAt = Date.now();
    this._markDirty();
  }

  private _onPointerDown = (e: PointerEvent): void => {
    const point = this._toStrokePoint(e);
    if (!point) return;

    e.preventDefault();
    try {
      this._canvas.setPointerCapture(e.pointerId);
    } catch {
      // Capture can fail in some embedded shells; drawing still works.
    }
    this._activePointerId = e.pointerId;
    this._hoverPoint = { x: point.x, y: point.y };

    if (this._doc.toolPrefs.tool === 'eraser') {
      this._eraseAt(point.x, point.y);
      return;
    }

    const strokeId = makeStrokeId();
    this._activeStrokeId = strokeId;
    this._ink.beginStroke(strokeId, this._currentInkStyle());
    this._ink.addPoint(point);
  };

  private _onPointerMove = (e: PointerEvent): void => {
    const point = this._toStrokePoint(e, this._activePointerId === e.pointerId);
    this._hoverPoint = point ? { x: point.x, y: point.y } : null;
    if (this._activePointerId !== e.pointerId || !point) return;

    e.preventDefault();
    if (this._doc.toolPrefs.tool === 'eraser') {
      this._eraseAt(point.x, point.y);
      return;
    }

    if (this._activeStrokeId) {
      this._ink.addPoint(point);
    }
  };

  private _onPointerUp = (e: PointerEvent): void => {
    if (this._activePointerId !== e.pointerId) return;
    e.preventDefault();
    const point = this._toPagePoint(e);
    this._hoverPoint = point ? { x: point.x, y: point.y } : null;
    this._finishStroke(true);
  };

  private _onPointerCancel = (e: PointerEvent): void => {
    if (this._activePointerId !== e.pointerId) return;
    this._hoverPoint = null;
    this._finishStroke(false);
  };

  private _drawScaffold(ctx: CanvasRenderingContext2D): void {
    const contentX = BENCH_LAYOUT.marginLeft;
    const contentY = BENCH_LAYOUT.marginTop;
    const contentW = BENCH_LAYOUT.width - BENCH_LAYOUT.marginLeft - BENCH_LAYOUT.marginRight;
    const contentH = BENCH_LAYOUT.height - BENCH_LAYOUT.marginTop - BENCH_LAYOUT.marginBottom;
    const railW = 170;
    const gap = 18;
    const scratchX = contentX + railW + gap;
    const scratchY = contentY + 94;
    const scratchW = contentW - railW - gap;
    const scratchH = contentH - 210;
    const footerY = scratchY + scratchH + 18;

    ctx.save();
    ctx.fillStyle = '#5d4227';
    ctx.font = '600 30px Georgia, serif';
    ctx.fillText('Experimental Bench', contentX, contentY + 20);

    ctx.fillStyle = 'rgba(84, 58, 31, 0.82)';
    ctx.font = 'italic 14px Georgia, serif';
    ctx.fillText('Basic scaffold only. No cadence, no spell loop, no hinting, no shape recognition.', contentX, contentY + 46);

    ctx.strokeStyle = 'rgba(100, 70, 38, 0.34)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(contentX, contentY + 64);
    ctx.lineTo(contentX + contentW, contentY + 64);
    ctx.stroke();

    ctx.fillStyle = 'rgba(126, 96, 54, 0.08)';
    ctx.fillRect(contentX, scratchY - 8, railW, scratchH + 8);
    ctx.strokeStyle = 'rgba(105, 78, 48, 0.26)';
    ctx.strokeRect(contentX, scratchY - 8, railW, scratchH + 8);

    ctx.fillStyle = '#6d4f2f';
    ctx.font = '600 16px Georgia, serif';
    ctx.fillText('Bench Frame', contentX + 14, scratchY + 18);

    ctx.font = '13px Georgia, serif';
    ctx.fillStyle = 'rgba(88, 64, 38, 0.92)';
    const notes = [
      'Use pads to try alternate loops.',
      'Keep only page, texture, and ink feel.',
      'Ignore magic, agents, hint rings, and shape workers here.',
      'Sketch mechanics directly on the page before wiring them into the journal.',
    ];
    let noteY = scratchY + 48;
    notes.forEach(note => {
      this._drawWrappedText(ctx, `• ${note}`, contentX + 14, noteY, railW - 28, 18);
      noteY += 52;
    });

    ctx.save();
    ctx.setLineDash([8, 8]);
    ctx.strokeStyle = 'rgba(98, 72, 42, 0.28)';
    ctx.strokeRect(scratchX, scratchY, scratchW, scratchH);
    ctx.strokeRect(scratchX, footerY, scratchW, 98);
    ctx.restore();

    ctx.fillStyle = '#6d4f2f';
    ctx.font = '600 15px Georgia, serif';
    ctx.fillText('Scratch Surface', scratchX + 14, scratchY + 22);
    ctx.fillText('Observations / Next Variant', scratchX + 14, footerY + 22);

    ctx.strokeStyle = 'rgba(110, 84, 48, 0.12)';
    for (let x = scratchX + 28; x < scratchX + scratchW; x += 32) {
      ctx.beginPath();
      ctx.moveTo(x, scratchY + 34);
      ctx.lineTo(x, scratchY + scratchH - 14);
      ctx.stroke();
    }
    for (let y = scratchY + 34; y < scratchY + scratchH; y += 28) {
      ctx.beginPath();
      ctx.moveTo(scratchX + 14, y);
      ctx.lineTo(scratchX + scratchW - 14, y);
      ctx.stroke();
    }

    const pad = this._activePad();
    if (pad) {
      ctx.fillStyle = 'rgba(86, 62, 34, 0.82)';
      ctx.font = 'italic 13px Georgia, serif';
      ctx.fillText(`Pad: ${pad.title}`, scratchX + scratchW - 140, contentY + 44);
    }

    ctx.restore();
  }

  private _drawWrappedText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
  ): void {
    const words = text.split(/\s+/);
    let line = '';
    let cursorY = y;
    words.forEach(word => {
      const next = line ? `${line} ${word}` : word;
      if (ctx.measureText(next).width <= maxWidth || line.length === 0) {
        line = next;
        return;
      }
      ctx.fillText(line, x, cursorY);
      cursorY += lineHeight;
      line = word;
    });
    if (line) ctx.fillText(line, x, cursorY);
  }

  private _drawEraserCursor(ctx: CanvasRenderingContext2D): void {
    if (this._doc.toolPrefs.tool !== 'eraser' || !this._hoverPoint) return;
    ctx.save();
    ctx.strokeStyle = 'rgba(128, 38, 24, 0.78)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.arc(this._hoverPoint.x, this._hoverPoint.y, this._eraserRadius(), 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private _renderLoop = (): void => {
    const dpr = window.devicePixelRatio || 1;
    const width = this._canvas.width / dpr;
    const height = this._canvas.height / dpr;
    const pad = this._activePad();
    const density = Math.min(1, (pad?.strokes.length ?? 0) / 80);

    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._ctx.clearRect(0, 0, width, height);

    const bg = this._ctx.createLinearGradient(0, 0, width, height);
    bg.addColorStop(0, '#1d1611');
    bg.addColorStop(1, '#281d15');
    this._ctx.fillStyle = bg;
    this._ctx.fillRect(0, 0, width, height);

    this._ctx.save();
    this._ctx.translate(this._pageOffsetX, this._pageOffsetY);
    this._ctx.scale(this._pageScale, this._pageScale);
    this._page.setPageColor(BENCH_PAGE_COLOR);
    this._page.drawPage(BENCH_LAYOUT, density);
    this._texture.draw(BENCH_LAYOUT, BENCH_PAGE_COLOR, 0.9);
    this._drawScaffold(this._ctx);
    this._ink.draw(0, 0);
    this._drawEraserCursor(this._ctx);
    this._ctx.restore();

    this._rafHandle = requestAnimationFrame(this._renderLoop);
  };
}
